/**
 * BacktestExplorer — Run backtests, view historical runs, inspect equity curve + trades.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Play, Loader2, History, Info } from 'lucide-react';
import EquityCurveChart from './EquityCurveChart';
import { timeAgo, formatPct } from './utils';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Reusable tooltip definitions for backtest parameters
const PARAM_TOOLTIPS = {
  years: 'Length of the walk-forward backtest window. 7 years covers multiple market regimes (2019 COVID crash, 2020 recovery, 2022 bear, 2024 bull).',
  top_n: 'Number of stocks held in the portfolio at any given time. Higher N = more diversification, lower concentration risk.',
  min_composite: 'Minimum composite factor score (0-100) a stock must have to be eligible for selection. Filters out low-conviction picks. 55 is a balanced default.',
  hysteresis: 'Prevents whipsaw trading. A candidate stock must score materially higher than the currently-held stock before swapping — avoids flip-flopping between similar-scored names and cuts turnover/slippage.',
  regime_filter: 'Go to cash (hold nothing) when NIFTY 50 is below its 200-day moving average. Most drawdowns happen in risk-off regimes — skipping them meaningfully improves Sharpe and max drawdown.',
  sector_cap: 'Caps the maximum number of picks from any single sector (default 5). Prevents over-concentration in a hot sector (e.g., IT or PSU banks) and improves portfolio resilience.',
  // Session 147j+13 — strict-gating tunables
  min_hold_days: 'Minimum calendar days a position must be held before churn-driven eviction. Stop-loss (-8%) overrides this. 0 = legacy hysteresis behaviour. 7 = current production setting. NOTE: under monthly rebalance + strict defaults (floor=80, delta=8), small mhd values may produce identical results — relax floor/delta below to see the impact.',
  rebalance_freq_days: 'How often to recheck/rebalance picks. Blank = monthly (legacy). 5 = weekly (matches typical hold periods). 1 = daily (highest churn).',
  strict_entry_floor: 'Absolute composite-score floor a candidate must clear to replace an incumbent. Blank = production default (80). Lower this (e.g. 70) to allow more swaps and see the effect of min_hold_days.',
  strict_delta_points: 'Minimum score margin a candidate must beat the incumbent by. Blank = production default (8). Lower (e.g. 0–3) to allow swaps on small score differences.',
};

const InfoIcon = ({ tooltip }) => (
  <span
    className="inline-flex ml-1 text-muted-foreground/60 hover:text-muted-foreground cursor-help"
    title={tooltip}
  >
    <Info className="w-3 h-3" />
  </span>
);

const BacktestExplorer = ({ profile = 'largecap' }) => {
  const { authFetch } = useAuth();
  const [runs, setRuns] = useState([]);
  const [selectedRun, setSelectedRun] = useState(null);
  const [trades, setTrades] = useState([]);
  const [running, setRunning] = useState(false);
  const [sweeping, setSweeping] = useState(false);          // 147j+13
  const [sweepResult, setSweepResult] = useState(null);     // 147j+13
  const [params, setParams] = useState({
    years: 7,
    top_n: 15,
    min_composite: 70,                  // Session 147j+13 — bumped 55→70
    use_hysteresis: true,
    use_regime_filter: true,
    use_sector_cap: false,
    max_per_sector: 5,
    min_hold_days: 7,                   // Session 147j+13 — strict gating default
    rebalance_freq_days: '',            // '' = monthly (legacy)
    strict_entry_floor: '',             // '' = use prod default (80)
    strict_delta_points: '',            // '' = use prod default (8)
  });

  // Session 147j Phase 2 — profile-aware backtest namespace.
  const profileQ = `profile=${encodeURIComponent(profile)}`;

  const loadRuns = useCallback(async () => {
    try {
      const r = await authFetch(`${API}/api/alphascout/backtest/runs?limit=20&${profileQ}`);
      const d = await r.json();
      setRuns(d.runs || []);
      if (d.runs?.length) {
        const sevenYearRun = d.runs.find((run) => Number(run.params?.years) === 7);
        const defaultRun = sevenYearRun || d.runs[0];
        await loadRun(defaultRun.run_id);
      } else {
        // No runs yet for this profile — clear stale selection from another profile
        setSelectedRun(null);
        setTrades([]);
      }
    } catch (e) {
      console.error('[BacktestExplorer] loadRuns failed', e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authFetch, profileQ]);

  const loadRun = async (runId) => {
    try {
      const [rRun, rTrades] = await Promise.all([
        authFetch(`${API}/api/alphascout/backtest/run/${runId}?${profileQ}`),
        authFetch(`${API}/api/alphascout/backtest/run/${runId}/trades?limit=500`),
      ]);
      const dRun = await rRun.json();
      const dTrades = await rTrades.json();
      setSelectedRun(dRun);
      setTrades(dTrades.trades || []);
    } catch (e) {
      console.error('[BacktestExplorer] loadRun failed', e);
    }
  };

  const runBacktest = async () => {
    setRunning(true);
    try {
      const q = new URLSearchParams({
        years: params.years,
        top_n: params.top_n,
        min_composite: params.min_composite,
        use_hysteresis: params.use_hysteresis,
        use_regime_filter: params.use_regime_filter,
        use_sector_cap: params.use_sector_cap,
        max_per_sector: params.max_per_sector,
        // Session 147j+13 — pass strict-gating params
        min_hold_days: params.min_hold_days,
        run_async: 'false',
        profile,
      });
      if (params.rebalance_freq_days !== '' && params.rebalance_freq_days != null) {
        q.set('rebalance_freq_days', String(params.rebalance_freq_days));
      }
      if (params.strict_entry_floor !== '' && params.strict_entry_floor != null) {
        q.set('strict_entry_floor', String(params.strict_entry_floor));
      }
      if (params.strict_delta_points !== '' && params.strict_delta_points != null) {
        q.set('strict_delta_points', String(params.strict_delta_points));
      }
      const r = await authFetch(`${API}/api/alphascout/backtest/run?${q.toString()}`, {
        method: 'POST',
      });
      const d = await r.json();
      if (d.status === 'ok') {
        // Refresh list AND explicitly load the newly-completed run so the
        // metrics card reflects the new params (fixes stale-selection bug
        // where changing Years gave the same visible result).
        // Session 147j+1 — CRITICAL: pass profileQ here too. Without it
        // the list re-fetch defaulted to largecap and the freshly-run
        // Hunter (or vice-versa) never showed up — making it look like
        // top_n changes had no effect.
        const listR = await authFetch(`${API}/api/alphascout/backtest/runs?limit=20&${profileQ}`);
        const listD = await listR.json();
        setRuns(listD.runs || []);
        if (d.run_id) {
          await loadRun(d.run_id);
        } else if (listD.runs?.length) {
          await loadRun(listD.runs[0].run_id);
        }
      } else {
        alert(`Backtest status: ${d.status}\n${d.message || ''}`);
      }
    } catch (e) {
      alert(`Backtest failed: ${e.message}`);
    } finally {
      setRunning(false);
    }
  };

  // Session 147j+13 — Holding-period sweep
  const runSweep = async () => {
    if (!window.confirm(
      'Run holding-period sweep?\n\n' +
      'This runs the backtest across [0,3,5,7,10,14,21] min_hold_days values\n' +
      'and identifies the optimal holding period by Sharpe + CAGR.\n\n' +
      `Estimated runtime: 7-21 minutes for ${params.years} years.\n` +
      'Other backtest controls (top_n, freq, etc.) follow your current settings.',
    )) return;
    setSweeping(true);
    setSweepResult(null);
    try {
      // 147j+13 — capture the CURRENT latest sweep_id before triggering, so
      // we can detect when the new sweep finishes and avoid showing the
      // previous sweep's stale results during the 5-15 min run window.
      let priorSweepId = null;
      try {
        const pr0 = await authFetch(
          `${API}/api/alphascout/backtest/sweep-latest?profile=${encodeURIComponent(profile)}`,
        );
        const pd0 = await pr0.json();
        priorSweepId = pd0?.sweep_id || null;
      } catch { /* noop */ }

      const q = new URLSearchParams({
        profile,
        years: params.years,
        top_n: params.top_n,
        rebalance_freq_days: params.rebalance_freq_days || 5,
        grid: '0,3,5,7,10,14,21',
      });
      const r = await authFetch(
        `${API}/api/alphascout/backtest/sweep-holding-period?${q.toString()}`,
        { method: 'POST' },
      );
      const d = await r.json();
      if (d.status !== 'triggered') {
        alert(`Sweep status: ${d.status}\n${d.message || ''}`);
        setSweeping(false);
        return;
      }
      // Poll
      const start = Date.now();
      const tick = async () => {
        try {
          const pr = await authFetch(
            `${API}/api/alphascout/backtest/sweep-latest?profile=${encodeURIComponent(profile)}`,
          );
          const pd = await pr.json();
          // Only accept once we see a NEW sweep_id (i.e. the one we just
          // triggered has finished writing to Mongo). Otherwise we'd flash
          // the previous run's stale results.
          if (
            pd.status === 'ok' && pd.results?.length
            && pd.sweep_id && pd.sweep_id !== priorSweepId
          ) {
            setSweepResult(pd);
            setSweeping(false);
            return;
          }
        } catch { /* noop */ }
        if (Date.now() - start > 30 * 60 * 1000) {
          setSweeping(false);
          alert('Sweep polling timed out after 30 min — check /backtest/sweep-latest manually.');
          return;
        }
        setTimeout(tick, 15 * 1000);
      };
      setTimeout(tick, 5 * 1000);
    } catch (e) {
      alert(`Sweep failed: ${e.message}`);
      setSweeping(false);
    }
  };

  useEffect(() => {
    // Session 147j+1 — clear stale selection IMMEDIATELY when the profile
    // prop changes. Otherwise the previously-loaded run's metrics card
    // remains rendered while the new profile's list is still fetching,
    // making it look like the result didn't change (the AlphaScout vs
    // Hunter cross-leak that produced the "top 10 == top 15" report).
    setSelectedRun(null);
    setTrades([]);
    loadRuns();
  }, [loadRuns]);

  const metrics = selectedRun?.metrics;

  return (
    <div className="space-y-4" data-testid="backtest-explorer">
      {/* Session 147j Phase 3 — survivorship-bias disclaimer (Hunter only) */}
      {profile === 'midsmall' && (
        <div
          className="border border-amber-500/40 bg-amber-500/10 px-4 py-3 flex items-start gap-3"
          data-testid="hunter-survivorship-disclaimer"
        >
          <span className="text-amber-400 text-lg leading-none mt-0.5">⚠</span>
          <div className="text-xs text-amber-100 leading-relaxed">
            <span className="font-bold uppercase tracking-wider text-amber-300 block mb-1">
              Survivorship Bias Notice
            </span>
            Universe is <span className="font-mono">current Nifty 500 ex-200</span> — survivorship bias
            likely inflates returns vs. point-in-time reality. Real out-of-sample CAGR may be{' '}
            <span className="font-semibold text-amber-200">40%–45%</span>.
          </div>
        </div>
      )}

      {/* Run controls */}
      <div className="bg-surface border border-border p-4">
        <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-3">
          New Walk-Forward Backtest
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-7 gap-3 items-end">
          <ParamInput
            label="Years"
            tooltip={PARAM_TOOLTIPS.years}
            value={params.years}
            onChange={(v) => setParams({ ...params, years: Math.max(1, Math.min(10, +v)) })}
            type="number"
          />
          <ParamInput
            label="Top N Picks"
            tooltip={PARAM_TOOLTIPS.top_n}
            value={params.top_n}
            onChange={(v) => setParams({ ...params, top_n: Math.max(3, Math.min(50, +v)) })}
            type="number"
          />
          <ParamInput
            label="Min Composite"
            tooltip={PARAM_TOOLTIPS.min_composite}
            value={params.min_composite}
            onChange={(v) => setParams({ ...params, min_composite: Math.max(0, Math.min(100, +v)) })}
            type="number"
          />
          <ParamInput
            label="Min Hold Days"
            tooltip={PARAM_TOOLTIPS.min_hold_days}
            value={params.min_hold_days}
            onChange={(v) => setParams({ ...params, min_hold_days: Math.max(0, Math.min(60, +v)) })}
            type="number"
          />
          <ParamInput
            label="Rebal Freq (d)"
            tooltip={PARAM_TOOLTIPS.rebalance_freq_days}
            value={params.rebalance_freq_days}
            onChange={(v) => setParams({ ...params, rebalance_freq_days: v === '' ? '' : Math.max(1, Math.min(63, +v)) })}
            type="number"
          />
          <ParamInput
            label="Strict Floor"
            tooltip={PARAM_TOOLTIPS.strict_entry_floor}
            value={params.strict_entry_floor}
            onChange={(v) => setParams({ ...params, strict_entry_floor: v === '' ? '' : Math.max(0, Math.min(100, +v)) })}
            type="number"
            placeholder="80"
          />
          <ParamInput
            label="Strict Delta"
            tooltip={PARAM_TOOLTIPS.strict_delta_points}
            value={params.strict_delta_points}
            onChange={(v) => setParams({ ...params, strict_delta_points: v === '' ? '' : Math.max(0, Math.min(50, +v)) })}
            type="number"
            placeholder="8"
          />
          <div>
            <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1 flex items-center">
              Hysteresis
              <InfoIcon tooltip={PARAM_TOOLTIPS.hysteresis} />
            </label>
            <button
              type="button"
              onClick={() => setParams({ ...params, use_hysteresis: !params.use_hysteresis })}
              className={`w-full px-3 py-2 text-sm border transition-colors ${
                params.use_hysteresis
                  ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400'
                  : 'bg-surface-highlight border-border text-muted-foreground'
              }`}
              data-testid="backtest-hysteresis-toggle"
              title={PARAM_TOOLTIPS.hysteresis}
            >
              {params.use_hysteresis ? 'ON' : 'OFF'}
            </button>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1 flex items-center">
              Regime Filter
              <InfoIcon tooltip={PARAM_TOOLTIPS.regime_filter} />
            </label>
            <button
              type="button"
              onClick={() => setParams({ ...params, use_regime_filter: !params.use_regime_filter })}
              className={`w-full px-3 py-2 text-sm border transition-colors ${
                params.use_regime_filter
                  ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400'
                  : 'bg-surface-highlight border-border text-muted-foreground'
              }`}
              data-testid="backtest-regime-toggle"
              title={PARAM_TOOLTIPS.regime_filter}
            >
              {params.use_regime_filter ? 'ON' : 'OFF'}
            </button>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1 flex items-center">
              Sector Cap
              <InfoIcon tooltip={PARAM_TOOLTIPS.sector_cap} />
            </label>
            <button
              type="button"
              onClick={() => setParams({ ...params, use_sector_cap: !params.use_sector_cap })}
              className={`w-full px-3 py-2 text-sm border transition-colors ${
                params.use_sector_cap
                  ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400'
                  : 'bg-surface-highlight border-border text-muted-foreground'
              }`}
              data-testid="backtest-sector-cap-toggle"
              title={params.use_sector_cap ? `Max ${params.max_per_sector} picks per sector. ${PARAM_TOOLTIPS.sector_cap}` : PARAM_TOOLTIPS.sector_cap}
            >
              {params.use_sector_cap ? `≤${params.max_per_sector}/sec` : 'OFF'}
            </button>
          </div>
          <button
            type="button"
            onClick={runBacktest}
            disabled={running}
            className="px-4 py-2 bg-accent text-background font-semibold text-sm flex items-center justify-center gap-2 hover:bg-accent/90 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="backtest-run-btn"
          >
            {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
            {running ? 'Running...' : 'Run Backtest'}
          </button>
          <button
            type="button"
            onClick={runSweep}
            disabled={running || sweeping}
            className="px-4 py-2 border border-cyan-500/40 text-cyan-300 font-semibold text-sm flex items-center justify-center gap-2 hover:bg-cyan-500/10 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="backtest-sweep-btn"
            title="Run backtest across multiple min_hold_days values to find the optimal holding period."
          >
            {sweeping ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
            {sweeping ? 'Sweeping...' : 'Sweep Hold Days'}
          </button>
        </div>
      </div>

      {sweepResult && (
        <SweepResultCard data={sweepResult} />
      )}

      {/* Runs list + Equity */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <div className="lg:col-span-1 bg-surface border border-border overflow-hidden">
          <div className="px-3 py-2 border-b border-border bg-surface-highlight flex items-center gap-2">
            <History className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
              Past Runs ({runs.length})
            </span>
          </div>
          <div className="max-h-[420px] overflow-y-auto divide-y divide-border/50">
            {runs.length === 0 && (
              <div className="p-4 text-xs text-muted-foreground">No runs yet.</div>
            )}
            {runs.map((r) => (
              <button
                key={r.run_id}
                onClick={() => loadRun(r.run_id)}
                className={`w-full text-left p-3 transition-colors ${
                  selectedRun?.run_id === r.run_id
                    ? 'bg-accent/10 border-l-2 border-accent -ml-px'
                    : 'hover:bg-surface-highlight'
                }`}
                data-testid={`backtest-run-${r.run_id.slice(0, 8)}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-mono text-foreground">
                    {r.run_id.slice(0, 8)}
                  </span>
                  <span className="text-[10px] text-muted-foreground">
                    {timeAgo(r.started_at)}
                  </span>
                </div>
                <div className="text-[10px] text-muted-foreground mb-1">
                  {r.range?.start} → {r.range?.end}
                </div>
                <div className="flex items-center gap-2 text-[10px]">
                  <span className="text-muted-foreground">
                    {r.params?.years}y · top-{r.params?.top_n}
                    {r.params?.min_hold_days != null && (
                      <span className="ml-1 text-cyan-400">
                        · hold≥{r.params.min_hold_days}d
                      </span>
                    )}
                    {r.params?.rebalance_freq_days != null && (
                      <span className="ml-1 text-cyan-400">
                        · rebal{r.params.rebalance_freq_days}d
                      </span>
                    )}
                    {r.params?.min_composite != null && (
                      <span className="ml-1 text-muted-foreground">
                        · ≥{r.params.min_composite}
                      </span>
                    )}
                  </span>
                  <span
                    className={`ml-auto font-mono font-bold ${
                      r.metrics?.total_return_pct > 0 ? 'text-emerald-400' : 'text-red-400'
                    }`}
                  >
                    {formatPct(r.metrics?.total_return_pct)}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="lg:col-span-3 space-y-2">
          {selectedRun?.params && (
            <div
              className="bg-surface border border-border px-3 py-2 text-[11px] font-mono text-muted-foreground flex flex-wrap gap-x-4 gap-y-1"
              data-testid="backtest-result-params"
            >
              <span>
                <span className="text-muted-foreground/60">Run:</span>{' '}
                <span className="text-foreground">{selectedRun.run_id?.slice(0, 8) || '—'}</span>
              </span>
              <span>
                <span className="text-muted-foreground/60">Years:</span>{' '}
                <span className="text-foreground">{selectedRun.params.years}</span>
              </span>
              <span>
                <span className="text-muted-foreground/60">Top-N:</span>{' '}
                <span className="text-foreground">{selectedRun.params.top_n}</span>
              </span>
              <span>
                <span className="text-muted-foreground/60">Min Composite:</span>{' '}
                <span className="text-foreground">{selectedRun.params.min_composite}</span>
              </span>
              {selectedRun.params.min_hold_days != null && (
                <span>
                  <span className="text-muted-foreground/60">Min Hold:</span>{' '}
                  <span className="text-cyan-300">
                    {selectedRun.params.min_hold_days}d
                    {selectedRun.params.min_hold_days === 0 && ' (legacy)'}
                  </span>
                </span>
              )}
              {selectedRun.params.rebalance_freq_days != null && (
                <span>
                  <span className="text-muted-foreground/60">Rebal Freq:</span>{' '}
                  <span className="text-cyan-300">{selectedRun.params.rebalance_freq_days}d</span>
                </span>
              )}
              {selectedRun.metrics?.days_held?.avg != null && (
                <span>
                  <span className="text-muted-foreground/60">Avg Held:</span>{' '}
                  <span className="text-foreground">{selectedRun.metrics.days_held.avg}d</span>
                </span>
              )}
              <span>
                <span className="text-muted-foreground/60">Rebalances:</span>{' '}
                <span className="text-foreground">{selectedRun.rebalance_count ?? '—'}</span>
              </span>
            </div>
          )}
          <EquityCurveChart curve={selectedRun?.equity_curve || []} metrics={metrics} />
        </div>
      </div>

      {/* Trade log preview */}
      {trades.length > 0 && (
        <div className="bg-surface border border-border overflow-hidden">
          <div className="px-3 py-2 border-b border-border bg-surface-highlight">
            <span className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
              Trade Log ({trades.length} trades — showing top 30 by return)
            </span>
          </div>
          <div className="max-h-[260px] overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-surface-highlight">
                <tr className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  <th className="px-3 py-1.5 text-left">Buy Date</th>
                  <th className="px-3 py-1.5 text-left">Sell Date</th>
                  <th className="px-3 py-1.5 text-left">Symbol</th>
                  <th className="px-3 py-1.5 text-right">Buy ₹</th>
                  <th className="px-3 py-1.5 text-right">Sell ₹</th>
                  <th className="px-3 py-1.5 text-right">Return</th>
                  <th className="px-3 py-1.5 text-right">Score</th>
                </tr>
              </thead>
              <tbody>
                {[...trades]
                  .sort((a, b) => Math.abs(b.return_pct) - Math.abs(a.return_pct))
                  .slice(0, 30)
                  .map((t, i) => (
                    <tr key={i} className="border-b border-border/30 hover:bg-surface-highlight">
                      <td className="px-3 py-1.5 font-mono text-muted-foreground">{t.buy_date}</td>
                      <td className="px-3 py-1.5 font-mono text-muted-foreground">{t.sell_date}</td>
                      <td className="px-3 py-1.5 font-bold">{t.symbol}</td>
                      <td className="px-3 py-1.5 text-right font-mono">{t.buy_price}</td>
                      <td className="px-3 py-1.5 text-right font-mono">{t.sell_price}</td>
                      <td
                        className={`px-3 py-1.5 text-right font-mono font-bold ${
                          t.return_pct > 0 ? 'text-emerald-400' : 'text-red-400'
                        }`}
                      >
                        {formatPct(t.return_pct)}
                      </td>
                      <td className="px-3 py-1.5 text-right font-mono">{t.composite_score}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

const ParamInput = ({ label, value, onChange, type = 'text', tooltip, placeholder }) => (
  <div>
    <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1 flex items-center">
      {label}
      {tooltip && <InfoIcon tooltip={tooltip} />}
    </label>
    <input
      type={type}
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-3 py-2 bg-surface-highlight border border-border text-sm text-foreground focus:outline-none focus:border-accent"
      title={tooltip}
    />
  </div>
);

// Session 147j+13 — Sweep results card
const SweepResultCard = ({ data }) => {
  if (!data?.results) return null;
  const best = data.best;
  const fmt = (v, suf = '') => v == null ? '—' : `${typeof v === 'number' ? v.toFixed(2) : v}${suf}`;
  return (
    <div
      className="border border-cyan-500/30 bg-cyan-500/5 p-4"
      data-testid="backtest-sweep-result"
    >
      <div className="flex items-baseline justify-between mb-3">
        <h3 className="text-sm font-mono uppercase tracking-wider text-cyan-300">
          Holding-Period Sweep · {data.profile} · {data.years}yr
        </h3>
        {best && (
          <div className="text-[11px] font-mono text-emerald-300">
            🏆 Optimal: <strong>{best.min_hold_days}d</strong>{' '}
            (Sharpe {fmt(best.sharpe)} · CAGR {fmt(best.cagr_pct, '%')})
          </div>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[11px] font-mono">
          <thead>
            <tr className="text-muted-foreground uppercase tracking-wider border-b border-border">
              <th className="text-left py-1.5 px-2">Hold (d)</th>
              <th className="text-right py-1.5 px-2">Total Ret</th>
              <th className="text-right py-1.5 px-2">CAGR</th>
              <th className="text-right py-1.5 px-2">Sharpe</th>
              <th className="text-right py-1.5 px-2">Max DD</th>
              <th className="text-right py-1.5 px-2">Win %</th>
              <th className="text-right py-1.5 px-2">Alpha</th>
              <th className="text-right py-1.5 px-2">Avg Held</th>
              <th className="text-right py-1.5 px-2">Rebal</th>
            </tr>
          </thead>
          <tbody>
            {data.results.map((r) => {
              const isBest = best && r.min_hold_days === best.min_hold_days;
              return (
                <tr
                  key={r.min_hold_days}
                  className={`border-b border-border/40 ${
                    isBest ? 'bg-emerald-500/10 text-emerald-300' : 'text-foreground'
                  }`}
                >
                  <td className="py-1.5 px-2 font-bold">
                    {isBest && '🏆 '}{r.min_hold_days}
                    {r.min_hold_days === 0 && (
                      <span className="text-muted-foreground"> (legacy)</span>
                    )}
                  </td>
                  <td className="text-right py-1.5 px-2">{fmt(r.total_return_pct, '%')}</td>
                  <td className="text-right py-1.5 px-2">{fmt(r.cagr_pct, '%')}</td>
                  <td className="text-right py-1.5 px-2">{fmt(r.sharpe)}</td>
                  <td className="text-right py-1.5 px-2 text-rose-300">{fmt(r.max_drawdown_pct, '%')}</td>
                  <td className="text-right py-1.5 px-2">{fmt(r.win_rate_pct, '%')}</td>
                  <td className="text-right py-1.5 px-2">{fmt(r.alpha_pct, '%')}</td>
                  <td className="text-right py-1.5 px-2">{fmt(r.avg_days_held)}</td>
                  <td className="text-right py-1.5 px-2">{r.rebalances ?? '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="mt-2 text-[10px] text-muted-foreground font-mono">
        Sweep grid: [{data.grid?.join(', ')}] · Rebalance freq: {data.rebalance_freq_days}d
        <br/>
        <span className="text-amber-300/70">
          ℹ️ Sweep relaxes other strict-gating constraints (delta=0, floor=70) to isolate hold-period impact.
          Values where min_hold_days &lt; rebalance_freq may show identical results — the lock never fires before the next rebalance window.
        </span>
      </div>
    </div>
  );
};

export default BacktestExplorer;
